import { useEffect, useRef, useState } from 'react';
import { onboardingSummary } from '../../redux-store/actions/application';
import { dashboardOnboardingType } from '../../redux-store/types/api-types';
import data from './data-ts';
import { useDispatch, useSelector } from 'react-redux';
import CenterTitlePlugin from './center-title-plugin-ts';
import AccessibleChartTitle from './AccessibleChartTitle';
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Card, Grid, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { RootStateType } from '../../redux-store/reducers';
import { USER_ROLES } from '../../utils/constant';
import {
  dashboardChartCardSx,
  buildDoughnutChartAriaLabel,
  chartCanvasFocusSx,
} from './chartAccessibleText';
import { useChartAriaLabel } from './useChartAriaLabel';
import { useChartKeyboardNavigation } from './useChartKeyboardNavigation';

ChartJS.register(...registerables);

export const dougnutType: dashboardOnboardingType = [
  {
    status: '',
    count: 0,
  },
];

export default function DoughnutGraph(): JSX.Element {
  const dispatch = useDispatch();
  const doughnutData = useSelector((store: RootStateType) => store.dataDougnut);
  const [dataDoughnut, SetDataDoughnut] = useState(dougnutType);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const isNarrowViewport = useMediaQuery('(max-width: 480px)');
  const isVeryNarrowViewport = useMediaQuery('(max-width: 320px)');
  const chartRef = useRef<ChartJS<any> | null>(null);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        (await dispatch(onboardingSummary())) as unknown as dashboardOnboardingType;
        if (!componentIsAlive) {
          return;
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, []);

  useEffect(() => {
    SetDataDoughnut(doughnutData);
  }, [doughnutData]);

  const titleText = isVeryNarrowViewport
    ? USER_ROLES.AMC_ADMIN === role
      ? ['Number of Applications', '(since inception)']
      : ['Number of Applications', '(onboarded by RM/SRM)']
    : USER_ROLES.AMC_ADMIN === role
    ? 'Number of Applications (since inception)'
    : 'Number of Applications (onboarded by RM/SRM)';
  const chartTitle = Array.isArray(titleText) ? titleText.join(' ') : titleText;
  const chartTitleId = 'doughnut-applications-chart-title';

  let completedApplication = 0;
  let inProgressApplication = 0;

  doughnutData?.map((data) => {
    if (data.status == 'completed') {
      completedApplication = data.count;
    }
    if (data.status == 'in_progress') {
      inProgressApplication = data.count;
    }
  });
  const applicationData = [completedApplication, inProgressApplication];
  const label = [
    [`Completed`, completedApplication],
    [`In Progress`, inProgressApplication],
  ];
  const options = {
    ...data.donutOptions,
    plugins: {
      ...data.donutOptions.plugins,
      legend: {
        ...data.donutOptions.plugins.legend,
        position: isNarrowViewport ? ('bottom' as const) : ('right' as const),
        labels: {
          ...data.donutOptions.plugins.legend.labels,
          padding: isNarrowViewport ? 8 : 20,
          ...(isVeryNarrowViewport ? { font: { size: 10, weight: 'bold' as const } } : {}),
        },
      },
      title: { ...data.donutOptions.plugins.title, display: false },
      centerTitle: {
        ...data.donutOptions.plugins.centerTitle,
        display: true,
        text: [
          String(
            applicationData.reduce<number>((sum, num) => (sum ? sum : 0) + (num ? num : 0), 0)
          ),
          'Total Applications',
        ],
        width: undefined,
      },
    },
    ...(isVeryNarrowViewport ? { cutout: 45 } : {}),
  };
  (data.donutData.datasets as any)[0].data = [completedApplication, inProgressApplication];
  const donutOptions = options as unknown as ChartOptions;

  (data.donutData.labels as any) = label;

  const chartAriaLabel = buildDoughnutChartAriaLabel(chartTitle, [
    { label: 'Completed applications', value: completedApplication },
    { label: 'In progress applications', value: inProgressApplication },
  ]);
  useChartAriaLabel(chartAriaLabel, chartRef);
  const isChartVisible =
    JSON.stringify(dougnutType) !== JSON.stringify(doughnutData) &&
    JSON.stringify(doughnutData) === JSON.stringify(dataDoughnut);
  useChartKeyboardNavigation(
    chartRef,
    {
      pointCount: applicationData.length,
      navigationMode: 'segment',
      enabled: isChartVisible,
    },
    chartReady
  );

  const setCombinedRef = (chartInstance: ChartJS | null | undefined) => {
    chartRef.current = chartInstance ?? null;
    setChartReady(!!chartInstance);
  };
  return (
    <Grid item xs={12} sm={12} md={12} lg={5} sx={{ maxWidth: '100%' }}>
      <Card
        raised={true}
        className="dashboard-chart-card"
        sx={{
          ...dashboardChartCardSx,
          minHeight: { xs: 380, sm: 270 },
          p: 2,
        }}>
        {JSON.stringify(dougnutType) === JSON.stringify(doughnutData) ||
        JSON.stringify(doughnutData) !== JSON.stringify(dataDoughnut) ? (
          <>
            <Skeleton />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                maxWidth: '100%',
                gap: 2,
              }}>
              <Skeleton
                variant="circular"
                sx={{
                  width: { xs: '60%', sm: 170 },
                  maxWidth: 170,
                  height: { xs: 'auto', sm: 170 },
                  aspectRatio: '1',
                  mt: 2,
                  mx: { xs: 'auto', sm: 'unset' },
                  ml: { sm: 15, lg: 2 },
                }}
              />
              <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Skeleton sx={{ width: '100%', maxWidth: 200 }} />
                <Skeleton sx={{ width: '40%', maxWidth: 50, ml: { xs: 0, sm: 5 } }} />
                <Skeleton sx={{ width: '100%', maxWidth: 200 }} />
                <Skeleton sx={{ width: '40%', maxWidth: 50, ml: { xs: 0, sm: 5 } }} />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <AccessibleChartTitle
              id={chartTitleId}
              text={titleText}
              fontSize={isVeryNarrowViewport ? 10 : { xs: 10, sm: 12 }}
              sx={{ mb: { xs: 1, sm: 0.5 } }}
            />
            <Typography
              component={'div'}
              className="canvas-container doughnut-canvas-container"
              sx={{
                '&.canvas-container': {
                  position: 'relative',
                  width: { xs: '300px', sm: 'unset' },
                  margin: 'auto',
                },
                ...chartCanvasFocusSx,
              }}>
              <Chart
                ref={setCombinedRef}
                height={isVeryNarrowViewport ? 300 : isNarrowViewport ? 260 : 200}
                type="doughnut"
                plugins={[CenterTitlePlugin]}
                data={data.donutData as ChartData}
                options={donutOptions}
              />
            </Typography>
          </>
        )}
      </Card>
    </Grid>
  );
}
